// ══════════════════════════════════════════════════════════════════
//  ED CRM - Hook de Leads (TanStack Query)
// ══════════════════════════════════════════════════════════════════
//  Lista os leads do workspace (com o sub-grafo de cards/meetings/tags),
//  assina realtime e expõe mutations otimistas.
//
//  Interface pública INALTERADA:
//    { leads, loading, error, fetchLeads, createLead, updateLead,
//      deleteLead, moveCard, passToCloser, patchLead }
// ══════════════════════════════════════════════════════════════════

import { useEffect, useMemo, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@hooks/useAuth';
import { qk } from '@/queries/keys';
import { useTeamMemberIds } from '@/queries/teamQueries';
import { isEnterpriseWorkspace } from '@config/enterprise';
import {
  listLeadsGraph,
  subscribeToTable,
  createLeadFlow,
  importLeadsFlow,
  updateLeadFlow,
  deleteLeadFlow,
  markLeadsLostFlow,
  moveCardStageFlow,
  passToCloserFlow,
} from '@app';
import type {
  LeadWithGraph,
  CreateLeadInput,
  LeadCreateContext,
  LeadUpdateInput,
  CardMover,
  LeadGraphVisibility,
  ImportLeadRow,
  ImportLeadsResult,
  ImportLeadsOptions,
  BulkLossLeadInput,
  BulkLossProgress,
  BulkLossResult,
  RealtimeChange,
} from '@app';

// Modelo de domínio agora vive em @modules/leads (src/modules/leads/leadModel.ts).
// Re-exportado aqui para não quebrar consumidores que importam de '@hooks/useLeads'.
export type {
  LeadWithGraph,
  LeadCardGraph,
  LeadTagGraph,
  LeadMeetingGraph,
  LeadTagInput,
  CreateLeadInput,
} from '@app';

// A normalização do grafo é responsabilidade do adapter; aqui é só passthrough.
export const fetchLeadsList = (
  workspaceId: string,
  visibility?: LeadGraphVisibility | null,
  pipelineIds?: string[] | null,
): Promise<LeadWithGraph[]> =>
  listLeadsGraph(workspaceId, visibility ?? null, pipelineIds ?? null);

// `scopeToVisibleLeads`: quando true, aplica no BACKEND o recorte de
// visibilidade Enterprise (não-gestor vê só leads do seu time / vinculados).
// Só o Kanban pede isso — as demais telas usam o fetch base inalterado.
//
// `pipelineIds`: restringe aos leads com card nesses pipelines. O Kanban passa
// os pipelines do funil aberto — ele desenha um funil por vez e não tem uso
// para o workspace inteiro. Passar o funil todo (e não só a aba ativa) faz
// alternar entre SDR e Closer não disparar refetch.
export interface UseLeadsOptions {
  scopeToVisibleLeads?: boolean;
  pipelineIds?: string[] | null;
}

export const useLeads = (options?: UseLeadsOptions) => {
  const auth = useAuth();
  const { workspace, loading: authLoading, user, userRole, profile, canViewAll } = auth;
  const queryClient = useQueryClient();

  const workspaceId = workspace?.id || null;

  // Recorte de visibilidade (Kanban Enterprise, não-gestor). Fora disso o
  // comportamento é idêntico ao anterior (fetch base, chave base).
  const { teamMemberIds, isLoading: teamLoading, enabled: teamEnabled } = useTeamMemberIds();
  const enterprise = isEnterpriseWorkspace(workspace);
  const role = typeof userRole === 'function' ? userRole() : null;
  const seeAll = typeof canViewAll === 'function' ? canViewAll() : false;
  const scoped = enterprise && !seeAll && (
    role === 'aex'
    || (!!options?.scopeToVisibleLeads && (role === 'sdr' || role === 'closer'))
  );

  const ownerIds = useMemo(
    () => (scoped
      ? (role === 'aex' ? [user?.id].filter(Boolean) as string[] : [...teamMemberIds].sort())
      : []),
    [scoped, role, user?.id, teamMemberIds],
  );
  // Recorte por pipeline (Kanban). Ordenado para a chave de cache não mudar só
  // porque a ordem do array veio diferente entre renders.
  const pipelineIds = useMemo(
    () => [...(options?.pipelineIds ?? [])].filter(Boolean).sort(),
    [options?.pipelineIds],
  );
  const pipeKey = pipelineIds.length ? `pipes:${pipelineIds.join(',')}` : null;

  const ownerKey = scoped ? `${role === 'aex' ? 'own' : 'team'}:${ownerIds.join(',')}` : null;
  const scopeKey = [ownerKey, pipeKey].filter(Boolean).join('|') || null;
  const queryKey = useMemo(
    () => (scopeKey
      ? qk.leadsScoped(workspaceId || '', scopeKey)
      : qk.leads(workspaceId || '')),
    [workspaceId, scopeKey],
  );
  const visibility: LeadGraphVisibility | null = scoped ? { ownerIds } : null;

  // No modo scoped, espera o time resolver antes de buscar (evita fetch com
  // conjunto parcial e um refetch logo em seguida).
  const teamReady = role === 'aex' || !teamEnabled || !teamLoading;

  const query = useQuery({
    queryKey,
    queryFn: () => fetchLeadsList(workspaceId || '', visibility, pipelineIds.length ? pipelineIds : null),
    enabled: !!workspaceId && !authLoading && (!scoped || (teamReady && ownerIds.length > 0)),
    // Refetch do grafo é caro. A frescura vem do Realtime (invalida em
    // leads/cards/lead_tags), então staleTime alto só evita refetches por
    // remontagem/navegação — sem risco de board desatualizado.
    staleTime: 2 * 60 * 1000,
  });

  const leads = useMemo<LeadWithGraph[]>(() => query.data ?? [], [query.data]);

  // Helpers de cache.
  const current = (): LeadWithGraph[] => queryClient.getQueryData<LeadWithGraph[]>(queryKey) || [];
  const setLeadsData = (updater: (list: LeadWithGraph[]) => LeadWithGraph[]) =>
    queryClient.setQueryData<LeadWithGraph[]>(queryKey, (old: LeadWithGraph[] | undefined) => updater(old || []));
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] });

  // ─── Realtime → invalidate (coalescido) ───────────────────────
  // Refetch do grafo de leads é caro (todos os leads ativos + relações
  // embutidas). As assinaturas de `cards`/`lead_tags` não têm workspace_id,
  // então chegam eventos de qualquer workspace; e uma carga em massa (import
  // cria N cards) emite uma rajada de eventos. Coalescemos num único refetch:
  // debounce de DEBOUNCE_MS, com teto de MAX_WAIT_MS para não ficar sem
  // atualizar sob stream contínuo. Durante um import local (importingRef) o
  // refetch por evento é suprimido — importLeads invalida uma vez no fim.
  const DEBOUNCE_MS = 1500;
  const MAX_WAIT_MS = 5000;
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const burstStartRef = useRef<number | null>(null);
  const importingRef = useRef(false);
  useEffect(() => {
    if (!workspaceId) return undefined;
    const run = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      burstStartRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['leads', workspaceId] });
    };
    const scheduleInvalidate = () => {
      if (importingRef.current) return; // import local: invalida uma vez no fim
      const now = Date.now();
      if (burstStartRef.current == null) burstStartRef.current = now;
      // Teto: se a rajada já dura MAX_WAIT_MS, força o refetch agora.
      if (now - burstStartRef.current >= MAX_WAIT_MS) { run(); return; }
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(run, DEBOUNCE_MS);
    };
    // cards/lead_tags não têm workspace_id → o Realtime não filtra no servidor.
    // Sem isso, uma escrita em QUALQUER workspace refazia o grafo em todos os
    // clientes. Filtramos no cliente pelo lead_id já conhecido no cache; quando
    // o lead_id não vem (ex.: DELETE sem REPLICA IDENTITY FULL) invalidamos por
    // segurança (raro). O canal de `leads` já é filtrado por workspace no server.
    const onGraphChange = (change?: RealtimeChange) => {
      const leadId = (change?.new?.lead_id ?? change?.old?.lead_id) as string | undefined;
      if (leadId) {
        // Checa todos os caches de leads deste workspace (base + scoped) pelo
        // prefixo — evita depender da queryKey exata capturada no closure.
        const caches = queryClient.getQueriesData<LeadWithGraph[]>({ queryKey: ['leads', workspaceId] });
        const known = caches.some(([, data]) => (data || []).some(l => l.id === leadId));
        if (!known) return;
      }
      scheduleInvalidate();
    };
    const unsubLeads = subscribeToTable({ table: 'leads', filter: `workspace_id=eq.${workspaceId}`, onChange: scheduleInvalidate });
    const unsubCards = subscribeToTable({ table: 'cards', onChange: onGraphChange });
    const unsubTags = subscribeToTable({ table: 'lead_tags', onChange: onGraphChange });
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      burstStartRef.current = null;
      unsubLeads();
      unsubCards();
      unsubTags();
    };
  }, [workspaceId, queryClient]);

  // ─── createLead (orquestração no LeadFlowService; hook só invalida) ─
  const createLead = async (leadData: CreateLeadInput) => {
    const wsSlug = workspace?.slug as string | undefined;
    if (!wsSlug) throw new Error('Workspace sem slug no hook useLeads.');
    const ctx: LeadCreateContext = {
      workspaceSlug: wsSlug,
      workspaceId: workspace?.id || '',
      teamRole: typeof userRole === 'function' ? userRole() : null,
      userId: user?.id ?? null,
      userEmail: user?.email ?? null,
      enterprise,
    };
    const result = await createLeadFlow(leadData, ctx);
    await invalidate();
    return { id: result.lead_id, lead_id: result.lead_id };
  };

  // ─── importLeads (carga em massa via CSV; invalida uma vez no fim) ─
  const importLeads = async (
    rows: ImportLeadRow[],
    onProgress?: (done: number, total: number) => void,
    options?: ImportLeadsOptions,
  ): Promise<ImportLeadsResult> => {
    const wsSlug = workspace?.slug as string | undefined;
    if (!wsSlug) throw new Error('Workspace sem slug no hook useLeads.');
    const ctx: LeadCreateContext = {
      workspaceSlug: wsSlug,
      workspaceId: workspace?.id || '',
      teamRole: typeof userRole === 'function' ? userRole() : null,
      userId: user?.id ?? null,
      userEmail: user?.email ?? null,
      enterprise,
    };
    // Suprime o refetch por evento de realtime durante a carga (cada linha
    // cria lead + card → rajada de eventos). O invalidate único ao fim faz o
    // refresh. `finally` garante que o gate reabre mesmo em erro/parcial.
    importingRef.current = true;
    try {
      return await importLeadsFlow(rows, ctx, onProgress, options);
    } finally {
      importingRef.current = false;
      await invalidate();
    }
  };

  // ─── updateLead (flow faz DB/webhook/propagação; hook faz otimista) ─
  const updateLead = async (leadId: string, updates: LeadUpdateInput) => {
    const prevStatus = (current().find(l => l.id === leadId)?.status as string | null) ?? null;
    await updateLeadFlow(leadId, updates, prevStatus);
    setLeadsData(list => list.map(l => (l.id === leadId ? { ...l, ...updates } : l)));
  };

  // ─── patchLead (só local — mudanças otimistas vindas de modais) ─
  const patchLead = useCallback((leadId: string, patch: LeadUpdateInput) => {
    queryClient.setQueryData<LeadWithGraph[]>(queryKey, (old: LeadWithGraph[] | undefined) =>
      (old || []).map(l => (l.id === leadId ? { ...l, ...patch } : l)));
  }, [queryClient, queryKey]);

  // ─── deleteLead (flow desativa lead+cards; hook remove do cache) ─
  const deleteLead = async (leadId: string) => {
    await deleteLeadFlow(leadId);
    setLeadsData(list => list.filter(l => l.id !== leadId));
  };

  // ─── moveCard (otimista + rollback; flow faz move/webhook/cancel) ─
  const moveCard = async (cardId: string, newStageId: string) => {
    const previousLeads = current();
    const leadRow = previousLeads.find(l => (l.cards || []).some(c => c.id === cardId));
    const cardRow = (leadRow?.cards || []).find(c => c.id === cardId);
    const oldStageId = cardRow?.stage_id || null;
    const leadId = (cardRow?.lead_id as string | undefined) || leadRow?.id || null;
    const mover: CardMover | null = user
      ? { id: user.id || null, nome: profile?.nome || null, email: user.email || null }
      : null;

    setLeadsData(list => list.map(lead => ({
      ...lead,
      cards: (lead.cards || []).map(card =>
        card.id === cardId ? { ...card, stage_id: newStageId } : card),
    })));

    try {
      await moveCardStageFlow({ cardId, newStageId, oldStageId, leadId, mover });
    } catch (err) {
      queryClient.setQueryData(queryKey, previousLeads);
      throw err;
    }
  };

  // ─── passToCloser (flow cria card closer + notifica; hook invalida) ─
  const passToCloser = async (leadId: string) => {
    const leadRow = current().find(l => l.id === leadId) || null;
    const prevStatus = (leadRow?.status as string | undefined) || null;
    const wsId = workspace?.id;
    if (!wsId) throw new Error('ID do Workspace não encontrado no hook useLeads.');
    await passToCloserFlow({
      workspaceId: wsId,
      leadId,
      sdrId: (leadRow?.sdr_id as string | undefined) || null,
      prevStatus,
    });
    await invalidate();
  };
  const markLeadsLostBulk = async (
    items: BulkLossLeadInput[],
    reason: string,
    onProgress?: (progress: BulkLossProgress) => void,
  ): Promise<BulkLossResult> => {
    const result = await markLeadsLostFlow(items, reason, onProgress);
    await invalidate();
    return result;
  };


  return {
    leads,
    loading: authLoading ? true : query.isLoading,
    error: query.error ? (query.error.message || 'Erro ao carregar leads.') : null,
    fetchLeads: invalidate,
    createLead,
    importLeads,
    updateLead,
    deleteLead,
    moveCard,
    passToCloser,
    patchLead,
    markLeadsLostBulk,
  };
};
